import { normalizeGender } from '@/utility/normalizeGender';
import { z } from 'zod';

const trimString = (u: unknown) => (typeof u === 'string' ? u.trim() : u);

const passwordMin = 8,
  passwordMax = 40,
  nameMin = 2,
  nameMax = 20;

const errorMessages = {
  firstNameMinError: `First Name must be at least ${nameMin} characters long`,
  firstNameMaxError: `First Name must be at most ${nameMax} characters long`,
  lastNameMinError: `Last Name must be at least ${nameMin} characters long`,
  lastNameMaxError: `Last Name must be at most ${nameMax} characters long`,
  passwordMinError: `Password must be at least ${passwordMin} characters long`,
  passwordMaxError: `Password must be at most ${passwordMax} characters long`,
  passwordAlphaNumericError: `Password must contain at least one letter and one number`,
  emailError: 'Email must be a valid email address',
  passwordMatchError: 'Passwords must match',
};

const hasNumber = new RegExp(/\d/);
const hasLetter = new RegExp(/[a-zA-Z]/);

export const userGetSchema = z
  .object({
    id: z.string().trim().cuid().optional().or(z.literal('')),
    email: z
      .string()
      .trim()
      .regex(/.+@.+\..+/i, { message: errorMessages.emailError })
      .optional()
      .or(z.literal('')),
  })
  .refine(
    (data) => data.id || data.email, // false triggers message
    'Either id, or email should be specified.'
  );

export const userLoginSchema = z.object({
  email: z
    .string()
    .trim()
    .regex(/.+@.+\..+/i, { message: errorMessages.emailError }),
  password: z
    .string()
    .trim()
    .min(passwordMin, { message: errorMessages.passwordMinError })
    .max(passwordMax, { message: errorMessages.passwordMaxError })
    .refine((value) => hasNumber.test(value) && hasLetter.test(value), {
      message: errorMessages.passwordAlphaNumericError,
    }),
});

export const userRegisterSchema = z
  .object({
    email: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .regex(/.+@.+\..+/i, { message: errorMessages.emailError })
    ),
    password: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .min(passwordMin, { message: errorMessages.passwordMinError })
        .max(passwordMax, { message: errorMessages.passwordMaxError })
        .refine((value) => hasNumber.test(value) && hasLetter.test(value), {
          message: errorMessages.passwordAlphaNumericError,
        })
    ),
    productKey: z.string().optional(),
    confirmPassword: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .min(passwordMin, { message: errorMessages.passwordMinError })
        .max(passwordMax, { message: errorMessages.passwordMaxError })
    ),
    firstName: z
      .string()
      .trim()
      .min(nameMin, {
        message: errorMessages.firstNameMinError,
      })

      .max(nameMax, {
        message: errorMessages.firstNameMaxError,
      }),
    lastName: z
      .string()
      .trim()
      .min(nameMin, {
        message: errorMessages.lastNameMinError,
      })
      .max(nameMax, {
        message: errorMessages.lastNameMaxError,
      }),

    gender: z.string().optional(),
    role: z.literal('TEACHER').optional(),
  })
  .refine((data) => data.confirmPassword === data.password, {
    message: errorMessages.passwordMatchError,
    path: ['confirmPassword'],
  });
export const publicUserRegisterSchema = userRegisterSchema
  .innerType()
  .pick({
    email: true,
    password: true,
    productKey: true,
    firstName: true,
    lastName: true,
  })
  .strict();
export const userUpdateSchema = z.object({
  oldPassword: z.preprocess(
    trimString,
    z
      .string()
      .trim()
      .min(passwordMin, { message: `Old ${errorMessages.passwordMinError}` })
      .max(passwordMax, { message: `Old ${errorMessages.passwordMaxError}` })
      .refine((value) => hasNumber.test(value) && hasLetter.test(value), {
        message: errorMessages.passwordAlphaNumericError,
      })
      .optional()
  ),
  // +
  newPassword: z.preprocess(
    trimString,
    z
      .string()
      .trim()
      .min(passwordMin, { message: `New ${errorMessages.passwordMinError}` })
      .max(passwordMax, { message: `New ${errorMessages.passwordMaxError}` })
      .refine((value) => hasNumber.test(value) && hasLetter.test(value), {
        message: errorMessages.passwordAlphaNumericError,
      })
      .optional()
  ),

  firstName: z
    .string()
    .trim()
    .min(nameMin, {
      message: errorMessages.firstNameMinError,
    })
    .max(nameMax, {
      message: errorMessages.firstNameMaxError,
    })
    .optional(),

  lastName: z
    .string()
    .trim()
    .min(nameMin, {
      message: errorMessages.lastNameMinError,
    })
    .max(nameMax, {
      message: errorMessages.lastNameMaxError,
    })
    .optional(),
  image: z.string().optional(),
  hasSeenOnBoarding: z.boolean().optional(),
  gender: z.preprocess(
    (val) => {
      if (typeof val !== 'string') return undefined;
      const t = val.trim();
      if (!t) return undefined; // allow blank -> undefined
      return normalizeGender(t);
    },
    z.enum(['male', 'female', 'other']).optional()
  ),
});

const passwordSchema = z
  .string()
  .trim()
  .refine(
    (value) => {
      if (!value) return true; // Skip if empty
      return value.length >= passwordMin;
    },
    { message: errorMessages.passwordMinError }
  )
  .refine(
    (value) => {
      if (!value) return true; // Skip if empty
      return value.length <= passwordMax;
    },
    { message: errorMessages.passwordMaxError }
  )
  .refine(
    (value) => {
      if (!value) return true; // Skip if empty
      return hasNumber.test(value) && hasLetter.test(value);
    },
    { message: errorMessages.passwordAlphaNumericError }
  );

export const updateStudentsSchema = z
  .object({
    password: z.preprocess(trimString, passwordSchema).optional(),
    confirmPassword: z
      .preprocess(
        trimString,
        z
          .string()
          .trim()
          .refine(
            (value) => {
              if (!value) return true; // Skip if empty
              return value.length >= passwordMin && value.length <= passwordMax;
            },
            {
              message: `Confirm Password ${errorMessages.passwordMinError} and ${errorMessages.passwordMaxError}`,
            }
          )
      )
      .optional(),
    firstName: z
      .string()
      .trim()
      .min(nameMin, {
        message: errorMessages.firstNameMinError,
      })

      .max(nameMax, {
        message: errorMessages.firstNameMaxError,
      }),
    lastName: z
      .string()
      .trim()
      .min(nameMin, {
        message: errorMessages.lastNameMinError,
      })
      .max(nameMax, {
        message: errorMessages.lastNameMaxError,
      }),
    email: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .regex(/.+@.+\..+/i, { message: errorMessages.emailError })
    ),
    gender: z
      .preprocess(
        trimString,
        z
          .string()
          .trim()
          .refine((value) => {
            if (!value) return true; // Skip if empty
            // allowed values are male, female or other
            return ['male', 'female', 'other'].includes(value.toLowerCase());
          })
      )
      .optional(),
  })
  .refine(
    (data) => {
      // Only check for password match if either of them is provided
      if (data.password || data.confirmPassword) {
        return data.confirmPassword === data.password;
      }
      return true;
    },
    {
      message: errorMessages.passwordMatchError,
      path: ['confirmPassword'],
    }
  );

export const createStudentSchema = z
  .object({
    password: z.preprocess(trimString, passwordSchema),
    confirmPassword: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .min(passwordMin, { message: errorMessages.passwordMinError })
        .max(passwordMax, { message: errorMessages.passwordMaxError })
    ),
    firstName: z
      .string()
      .trim()
      .min(nameMin, {
        message: errorMessages.firstNameMinError,
      })

      .max(nameMax, {
        message: errorMessages.firstNameMaxError,
      }),
    lastName: z
      .string()
      .trim()
      .min(nameMin, {
        message: errorMessages.lastNameMinError,
      })
      .max(nameMax, {
        message: errorMessages.lastNameMaxError,
      }),
    email: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .regex(/.+@.+\..+/i, { message: errorMessages.emailError })
    ),
    gender: z
      .preprocess(
        trimString,
        z
          .string()
          .trim()
          .refine((value) => {
            if (!value) return true;
            return ['male', 'female', 'other'].includes(value.toLowerCase());
          })
      )
      .optional(),
  })
  .refine((data) => data.confirmPassword === data.password, {
    message: errorMessages.passwordMatchError,
    path: ['confirmPassword'],
  });

export const userForgotPasswordSchema = z.object({
  email: z.preprocess(trimString, z.string().trim().email()),
});

export const userResendVerificationSchema = userForgotPasswordSchema;

export const userResetPasswordSchema = z
  .object({
    password: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .min(passwordMin, { message: errorMessages.passwordMinError })
        .max(passwordMax, { message: errorMessages.passwordMaxError })
        .refine((value) => hasNumber.test(value) && hasLetter.test(value), {
          message: errorMessages.passwordAlphaNumericError,
        })
    ),
    confirmPassword: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .min(passwordMin, { message: errorMessages.passwordMinError })
        .max(passwordMax, { message: errorMessages.passwordMaxError })
    ),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: errorMessages.passwordMatchError,
    path: ['confirmPassword'],
  });

const studentFirstNameSchema = z
  .string()
  .trim()
  .min(nameMin, {
    message: errorMessages.firstNameMinError,
  })
  .max(nameMax, {
    message: errorMessages.firstNameMaxError,
  });

const studentLastNameSchema = z
  .string()
  .trim()
  .min(nameMin, {
    message: errorMessages.lastNameMinError,
  })
  .max(nameMax, {
    message: errorMessages.lastNameMaxError,
  });

const studentClassroomIdSchema = z
  .string()
  .trim()
  .cuid({ message: 'Invalid classroom id' });

export const StudentUpsertSchema = z
  .object({
    email: z.preprocess(trimString, z.string().trim().email()),
    password: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .min(passwordMin, { message: errorMessages.passwordMinError })
        .max(passwordMax, { message: errorMessages.passwordMaxError })
        .refine((value) => hasNumber.test(value) && hasLetter.test(value), {
          message: errorMessages.passwordAlphaNumericError,
        })
    ),
    confirmPassword: z.preprocess(
      trimString,
      z
        .string()
        .trim()
        .min(passwordMin, { message: errorMessages.passwordMinError })
        .max(passwordMax, { message: errorMessages.passwordMaxError })
    ),
    firstName: studentFirstNameSchema,
    lastName: studentLastNameSchema,
    gender: z.preprocess(
      (val) => {
        if (typeof val !== 'string') return undefined;
        const t = val.trim().toLowerCase();
        if (!t) return undefined; // allow blank -> undefined
        return t;
      },
      z.enum(['male', 'female', 'other']).optional()
    ),
    teacherId: z
      .string()
      .trim()
      .cuid({ message: 'Invalid teacher id' })
      .optional(),
    classroomId: studentClassroomIdSchema,
  })
  .refine((data) => data.confirmPassword === data.password, {
    message: errorMessages.passwordMatchError,
    path: ['confirmPassword'],
  });

export type SingleStudentInput = z.infer<typeof StudentUpsertSchema>;

export const studentNameUpdateSchema = z.object({
  classroomId: studentClassroomIdSchema,
  firstName: studentFirstNameSchema,
  lastName: studentLastNameSchema,
});

export const StudentCsvUpsertSchema = z.object({
  email: z.preprocess(trimString, z.string().trim().email()),
  first_name: z
    .string()
    .trim()
    .min(nameMin, {
      message: errorMessages.firstNameMinError,
    })
    .max(nameMax, {
      message: errorMessages.firstNameMaxError,
    }),
  last_name: z
    .string()
    .trim()
    .min(nameMin, {
      message: errorMessages.lastNameMinError,
    })
    .max(nameMax, {
      message: errorMessages.lastNameMaxError,
    }),
  password: z.preprocess(
    trimString,
    z
      .string()
      .trim()
      .min(passwordMin, { message: errorMessages.passwordMinError })
      .max(passwordMax, { message: errorMessages.passwordMaxError })
      .refine((value) => hasNumber.test(value) && hasLetter.test(value), {
        message: errorMessages.passwordAlphaNumericError,
      })
  ),
  gender: z.preprocess(
    (val) => {
      if (typeof val !== 'string') return undefined;
      const t = val.trim();
      if (!t) return undefined; // allow blank -> undefined
      return normalizeGender(t);
    },
    z.enum(['male', 'female', 'other']).optional()
  ),
});

export * from './validationClassroomTaskSchemas';
export * from './validationAssignmentOrganizationSchemas';
export * from './validationReportSchemas';
export * from './validation/aiNotebookSchemas';
